'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AlertCircle,
  Loader2,
  CheckCircle2,
  ChevronDown,
  HelpCircle,
} from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';
import {
  useCreateWoocommerceWebhooks,
  useCheckWoocommercePermissions,
  useManualConnectWoocommerce,
} from '@/lib/api/integrations';
import { trpc } from '@/lib/api/trpc';
import { Badge } from '@/components/ui/badge';
import { useEffectiveBranding } from '@/hooks/use-effective-branding';
import { WooCommerceCheckoutPlugins } from './woocommerce-checkout-plugins';
import { derivePluginVersionState, parseInstalledPluginVersion } from '@/lib/woocommerce-plugin-version';

// ============================================================================
// Types & Schema
// ============================================================================

const createFormSchema = (t: (key: string) => string) =>
  z.object({
    url: z.string().min(1, t('woocommerce.validation.shopUrlRequired')),
  });

const manualFormSchema = (t: (key: string) => string) =>
  z.object({
    url: z.string().startsWith('https', t('woocommerce.manual.urlMustBeHttps')),
    consumerKey: z.string().min(1, t('woocommerce.manual.consumerKeyRequired')),
    consumerSecret: z.string().min(1, t('woocommerce.manual.consumerSecretRequired')),
  });

const editFormSchema = (t: (key: string) => string) =>
  z.object({
    url: z.string().startsWith('https', t('woocommerce.manual.urlMustBeHttps')),
    consumerKey: z.string().min(1, t('woocommerce.manual.consumerKeyRequired')),
    consumerSecret: z.string().min(1, t('woocommerce.manual.consumerSecretRequired')),
    syncOrdersType: z.enum(['pending', 'processing', 'completed']),
    pushOrder: z.boolean(),
    pushRebillOrders: z.boolean(),
    autoSyncEnabled: z.boolean(),
    useNativeOrderEmails: z.boolean(),
    dynamicDiscountEnabled: z.boolean(),
    dynamicDiscountStrategy: z.enum(['order-level', 'line-item-allocation']),
  });

type CreateFormValues = { url: string };
type ManualFormValues = { url: string; consumerKey: string; consumerSecret: string };
type EditFormValues = {
  url: string;
  consumerKey: string;
  consumerSecret: string;
  syncOrdersType: 'pending' | 'processing' | 'completed';
  pushOrder: boolean;
  pushRebillOrders: boolean;
  autoSyncEnabled: boolean;
  useNativeOrderEmails: boolean;
  dynamicDiscountEnabled: boolean;
  dynamicDiscountStrategy: 'order-level' | 'line-item-allocation';
};

interface WoocommerceFormProps {
  defaultValues?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  isLoading?: boolean;
  storeId?: string;
  accountId?: string;
  /** When the integration row was created — decides whether "never reported a version" means stale. */
  integrationCreatedAt?: Date | string | null;
}

type PermalinkError = 'invalid_url' | 'permalinks' | null;

// ============================================================================
// Component
// ============================================================================

export function WoocommerceForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
  storeId,
  accountId,
  integrationCreatedAt,
}: WoocommerceFormProps) {
  const { toast } = useToast();
  const { t } = useTranslation('integrations');
  const brand = useEffectiveBranding();
  const [isConnecting, setIsConnecting] = useState(false);
  const [authorizationUrl, setAuthorizationUrl] = useState<string | null>(null);
  const [permalinkError, setPermalinkError] = useState<PermalinkError>(null);
  const createWebhooks = useCreateWoocommerceWebhooks();

  // Extract storeId from defaultValues if not provided as prop
  const resolvedStoreId = storeId || (defaultValues as any)?.storeId;
  const resolvedAccountId = accountId || (defaultValues as any)?.accountId;

  // Check if integration exists (has credentials)
  const hasCredentials = !!(
    defaultValues.credentials?.url &&
    defaultValues.credentials?.consumerKey &&
    defaultValues.credentials?.consumerSecret
  );

  const isEditMode = hasCredentials;

  const permissionsCheck = useCheckWoocommercePermissions(
    resolvedStoreId ?? '',
    isEditMode && !!resolvedStoreId,
  );

  const installedPluginVersion = parseInstalledPluginVersion(defaultValues.metadata?.pluginVersion);

  const currentPluginVersionsQuery = trpc.woocommerceScript.getCurrentPluginVersions.useQuery(undefined, {
    enabled: isEditMode,
    staleTime: 5 * 60 * 1000,
  });

  const currentPluginVersion = installedPluginVersion
    ? currentPluginVersionsQuery.data?.versions[installedPluginVersion.slug]
    : undefined;

  const pluginVersionState = derivePluginVersionState({
    installed: installedPluginVersion,
    current: currentPluginVersionsQuery.data,
    isLoading: currentPluginVersionsQuery.isLoading,
    integrationCreatedAt,
  });

  // Create mode form
  const createForm = useForm<CreateFormValues>({
    resolver: zodResolver(createFormSchema(t)),
    defaultValues: {
      url: '',
    },
  });

  // Manual mode form
  const manualForm = useForm<ManualFormValues>({
    resolver: zodResolver(manualFormSchema(t)),
    defaultValues: {
      url: '',
      consumerKey: '',
      consumerSecret: '',
    },
  });

  const manualConnect = useManualConnectWoocommerce();

  // Edit mode form
  const editForm = useForm<EditFormValues>({
    resolver: zodResolver(editFormSchema(t)),
    defaultValues: {
      url: defaultValues.credentials?.url ?? '',
      consumerKey: defaultValues.credentials?.consumerKey ?? '',
      consumerSecret: defaultValues.credentials?.consumerSecret ?? '',
      syncOrdersType: defaultValues.credentials?.syncOrdersType ?? 'processing',
      pushOrder: defaultValues.credentials?.pushOrder ?? true,
      pushRebillOrders: defaultValues.credentials?.pushRebillOrders ?? true,
      autoSyncEnabled: defaultValues.credentials?.autoSyncEnabled ?? false,
      useNativeOrderEmails: defaultValues.settings?.useNativeOrderEmails ?? false,
      dynamicDiscountEnabled: defaultValues.credentials?.dynamicDiscountEnabled ?? false,
      dynamicDiscountStrategy: defaultValues.credentials?.dynamicDiscountStrategy ?? 'order-level',
    },
  });

  const watchUrl = createForm.watch('url');

  // Check permalinks before connecting
  const checkPermalinks = async (url: string): Promise<{ success: boolean; error?: PermalinkError }> => {
    try {
      // // Normalize URL
      // let normalizedUrl = url.trim()
      // if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      //   normalizedUrl = `https://${normalizedUrl}`
      // }

      // const response = await fetch(normalizedUrl, { method: 'HEAD' })
      // const linkHeader = response.headers.get('link')

      // if (!linkHeader) {
      //   // No link header, assume it's okay
      //   return { success: true }
      // }

      // // Parse Link header for WordPress API
      // const apiLinkMatch = linkHeader.match(/<([^>]+)>;\s*rel="https:\/\/api\.w\.org\/"/)
      // if (apiLinkMatch) {
      //   const apiUrl = new URL(apiLinkMatch[1])
      //   if (apiUrl.searchParams.has('rest_route')) {
      //     return { success: false, error: 'permalinks' }
      //   }
      // }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'invalid_url' };
    }
  };

  // Handle create mode submission (connect to WooCommerce)
  const handleCreateSubmit = async (data: CreateFormValues) => {
    setIsConnecting(true);
    setPermalinkError(null);

    try {
      // Validate that we have the required IDs
      if (!resolvedStoreId || !resolvedAccountId) {
        toast({
          title: t('woocommerce.errors.toastTitle'),
          description: t('woocommerce.errors.missingIds'),
          variant: 'destructive',
        });
        setIsConnecting(false);
        return;
      }

      // Check permalinks first
      const permalinkCheck = await checkPermalinks(data.url);
      if (!permalinkCheck.success) {
        setPermalinkError(permalinkCheck.error ?? null);
        createForm.setError('url', {
          type: permalinkCheck.error ?? 'invalid_url',
          message:
            permalinkCheck.error === 'invalid_url'
              ? t('woocommerce.validation.invalidUrl')
              : t('woocommerce.validation.invalidPermalinks'),
        });
        setIsConnecting(false);
        return;
      }

      // Normalize URL — strip path/query/hash, keep only origin
      let normalizedUrl = data.url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }
      normalizedUrl = new URL(normalizedUrl).origin;

      // Generate encoded userId (matching v1 WoocommerceUserId.toWoocommerceUserId())
      const userIdPayload = JSON.stringify({
        accountId: resolvedAccountId,
        storeId: resolvedStoreId,
      });
      const encodedUserId = btoa(userIdPayload);

      // Construct OAuth URL
      const baseUrl = new URL(normalizedUrl);
      const callbackUrl = `${import.meta.env.VITE_LOCAL_TUNNEL_BASE_URL ?? import.meta.env.VITE_API_URL}/api/public/oauth/woocommerce/callback?store_url=${normalizedUrl}`;
      const returnUrl = window.location.href;

      // const authUrl = new URL(`${baseUrl.origin}/wc-auth/v1/authorize`)
      const authUrl = new URL(`${baseUrl.origin}/wc-auth/v1/authorize`);
      authUrl.searchParams.set('app_name', brand.appName);
      authUrl.searchParams.set('scope', 'read_write');
      authUrl.searchParams.set('user_id', encodedUserId);
      authUrl.searchParams.set('return_url', returnUrl);
      authUrl.searchParams.set('callback_url', callbackUrl);

      setAuthorizationUrl(authUrl.toString());
    } catch (error: any) {
      toast({
        title: t('woocommerce.errors.toastTitle'),
        description: error.message || t('woocommerce.errors.failedConnection'),
        variant: 'destructive',
      });
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle edit mode submission (update settings)
  const handleEditSubmit = async (data: EditFormValues) => {
    try {
      await onSubmit({
        credentials: {
          ...defaultValues.credentials,
          url: new URL(data.url).origin,
          consumerKey: data.consumerKey,
          consumerSecret: data.consumerSecret,
          syncOrdersType: data.syncOrdersType,
          pushOrder: data.pushOrder,
          pushRebillOrders: data.pushRebillOrders,
          autoSyncEnabled: data.autoSyncEnabled,
          dynamicDiscountEnabled: data.dynamicDiscountEnabled,
          dynamicDiscountStrategy: data.dynamicDiscountStrategy,
        },
        settings: {
          ...defaultValues.settings,
          useNativeOrderEmails: data.useNativeOrderEmails,
        },
      });
    } catch (error: any) {
      toast({
        title: t('woocommerce.errors.toastTitle'),
        description: error.message || t('woocommerce.errors.failedUpdate'),
        variant: 'destructive',
      });
    }
  };

  // Handle OAuth dialog redirect
  const handleOAuthRedirect = () => {
    if (authorizationUrl) {
      window.location.href = authorizationUrl;
    }
  };

  // Handle manual mode submission
  const handleManualSubmit = async (data: ManualFormValues) => {
    if (!resolvedStoreId) {
      toast({
        title: t('woocommerce.errors.toastTitle'),
        description: t('woocommerce.errors.missingIds'),
        variant: 'destructive',
      });
      return;
    }
    await manualConnect.mutateAsync({
      storeId: resolvedStoreId,
      url: new URL(data.url).origin,
      consumerKey: data.consumerKey,
      consumerSecret: data.consumerSecret,
    });
  };

  // Handle webhook generation / replacement
  const handleGenerateWebhooks = async () => {
    if (!resolvedStoreId) {
      toast({
        title: t('woocommerce.errors.toastTitle'),
        description: t('woocommerce.errors.missingIds'),
        variant: 'destructive',
      });
      return;
    }
    await createWebhooks.mutateAsync({ storeId: resolvedStoreId });
  };

  const hasWebhookSecret = !!defaultValues.credentials?.webhookSecret;

  // Render CREATE MODE
  if (!isEditMode) {
    return (
      <>
        <Tabs defaultValue="oauth" className="space-y-6">
          <TabsList>
            <TabsTrigger value="oauth">{t('woocommerce.create.oauthTab')}</TabsTrigger>
            <TabsTrigger value="manual">{t('woocommerce.create.manualTab')}</TabsTrigger>
          </TabsList>

          {/* OAuth Tab */}
          <TabsContent value="oauth">
            <form
              id="integration-config-form"
              onSubmit={createForm.handleSubmit(handleCreateSubmit)}
              className="space-y-6"
            >
              {/* Permalink Error Alert */}
              {permalinkError === 'permalinks' && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t('woocommerce.permalinks.title')}</AlertTitle>
                  <AlertDescription>
                    <p className="mb-2">{t('woocommerce.permalinks.description')}</p>
                    <ul className="list-inside list-disc space-y-1 text-sm">
                      <li>{t('woocommerce.permalinks.dayAndName')}</li>
                      <li>{t('woocommerce.permalinks.monthAndName')}</li>
                      <li>{t('woocommerce.permalinks.numeric')}</li>
                      <li>{t('woocommerce.permalinks.postName')}</li>
                    </ul>
                    <p className="mt-2">
                      {t('woocommerce.permalinks.goToBackOffice')}{' '}
                      {watchUrl ? (
                        <a
                          href={`${watchUrl}/wp-admin/options-permalink.php`.replace(/\/+/g, '/')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary font-medium underline"
                        >
                          /wp-admin/options-permalink.php
                        </a>
                      ) : (
                        <span className="font-medium">/wp-admin/options-permalink.php</span>
                      )}{' '}
                      {t('woocommerce.permalinks.toChangeSetting')}
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* Connection Card */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('woocommerce.create.title')}</CardTitle>
                  <CardDescription>{t('woocommerce.create.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="wooBaseUrl">
                      {t('woocommerce.create.shopUrlLabel')}
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input
                      id="wooBaseUrl"
                      placeholder={t('woocommerce.create.shopUrlPlaceholder')}
                      disabled={isConnecting || isLoading}
                      {...createForm.register('url')}
                    />
                    {createForm.formState.errors.url && permalinkError !== 'permalinks' && (
                      <p className="text-destructive text-xs">{createForm.formState.errors.url.message}</p>
                    )}
                  </div>

                  <Button type="submit" disabled={isConnecting || isLoading || !createForm.formState.isDirty}>
                    {isConnecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isConnecting
                      ? t('woocommerce.create.connecting')
                      : t('woocommerce.create.connectButton')}
                  </Button>
                </CardContent>
              </Card>
            </form>
          </TabsContent>

          {/* Manual Tab */}
          <TabsContent value="manual">
            <form onSubmit={manualForm.handleSubmit(handleManualSubmit)} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('woocommerce.manual.title')}</CardTitle>
                  <CardDescription>{t('woocommerce.manual.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <ul className="list-inside list-disc space-y-1 text-sm">
                        <li>{t('woocommerce.manual.requireAdmin')}</li>
                        <li>{t('woocommerce.manual.requireReadWrite')}</li>
                      </ul>
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label htmlFor="manualUrl">
                      {t('woocommerce.manual.urlLabel')}
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input
                      id="manualUrl"
                      placeholder={t('woocommerce.manual.urlPlaceholder')}
                      disabled={manualConnect.isPending || isLoading}
                      {...manualForm.register('url')}
                    />
                    {manualForm.formState.errors.url && (
                      <p className="text-destructive text-xs">{manualForm.formState.errors.url.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="manualConsumerKey">
                      {t('woocommerce.manual.consumerKeyLabel')}
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input
                      id="manualConsumerKey"
                      placeholder={t('woocommerce.manual.consumerKeyPlaceholder')}
                      disabled={manualConnect.isPending || isLoading}
                      {...manualForm.register('consumerKey')}
                    />
                    {manualForm.formState.errors.consumerKey && (
                      <p className="text-destructive text-xs">
                        {manualForm.formState.errors.consumerKey.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="manualConsumerSecret">
                      {t('woocommerce.manual.consumerSecretLabel')}
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input
                      id="manualConsumerSecret"
                      type="password"
                      placeholder={t('woocommerce.manual.consumerSecretPlaceholder')}
                      disabled={manualConnect.isPending || isLoading}
                      {...manualForm.register('consumerSecret')}
                    />
                    {manualForm.formState.errors.consumerSecret && (
                      <p className="text-destructive text-xs">
                        {manualForm.formState.errors.consumerSecret.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={manualConnect.isPending || isLoading || !manualForm.formState.isDirty}
                  >
                    {manualConnect.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {manualConnect.isPending
                      ? t('woocommerce.manual.connecting')
                      : t('woocommerce.manual.connectButton')}
                  </Button>
                </CardContent>
              </Card>
            </form>
          </TabsContent>
        </Tabs>

        {/* Troubleshooting Guide */}
        <WooCommerceTroubleshootGuide />

        {/* OAuth Redirect Dialog */}
        <Dialog open={!!authorizationUrl} onOpenChange={(open) => !open && setAuthorizationUrl(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('woocommerce.oauth.title')}</DialogTitle>
              <DialogDescription>{t('woocommerce.oauth.description')}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAuthorizationUrl(null)}>
                {t('woocommerce.oauth.cancel')}
              </Button>
              <Button onClick={handleOAuthRedirect}>{t('woocommerce.oauth.continue')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  // Render EDIT MODE
  return (
    <form
      id="integration-config-form"
      onSubmit={editForm.handleSubmit(handleEditSubmit)}
      className="space-y-6"
    >
      {/* Connected Store Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('woocommerce.edit.title')}</CardTitle>
          <CardDescription>{t('woocommerce.edit.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="wooShopUrlInput">
              {t('woocommerce.edit.shopUrlLabel')}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Input
              id="wooShopUrlInput"
              placeholder={t('woocommerce.manual.urlPlaceholder')}
              disabled={isLoading}
              {...editForm.register('url')}
            />
            {editForm.formState.errors.url && (
              <p className="text-destructive text-xs">{editForm.formState.errors.url.message}</p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="wooClientKey">
                {t('woocommerce.edit.clientKey')}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="wooClientKey"
                placeholder={t('woocommerce.manual.consumerKeyPlaceholder')}
                disabled={isLoading}
                {...editForm.register('consumerKey')}
              />
              {editForm.formState.errors.consumerKey && (
                <p className="text-destructive text-xs">
                  {editForm.formState.errors.consumerKey.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="wooClientSecret">
                {t('woocommerce.edit.clientSecret')}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="wooClientSecret"
                type="password"
                placeholder={t('woocommerce.manual.consumerSecretPlaceholder')}
                disabled={isLoading}
                {...editForm.register('consumerSecret')}
              />
              {editForm.formState.errors.consumerSecret && (
                <p className="text-destructive text-xs">
                  {editForm.formState.errors.consumerSecret.message}
                </p>
              )}
            </div>
          </div>
          <p className="text-muted-foreground text-xs">{t('woocommerce.edit.credentialsHelp')}</p>

          {/* Permissions Check */}
          <div className="flex items-center gap-2 pt-2">
            {permissionsCheck.isLoading && (
              <>
                <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />
                <span className="text-muted-foreground text-xs">{t('woocommerce.permissions.checking')}</span>
              </>
            )}
            {permissionsCheck.data?.hasPermission === true && (
              <Badge variant="green">
                <CheckCircle2 className="h-3 w-3" />
                {t('woocommerce.permissions.valid')}
              </Badge>
            )}
            {permissionsCheck.data?.hasPermission === false && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t('woocommerce.permissions.invalidTitle')}</AlertTitle>
                <AlertDescription>{permissionsCheck.data.reason}</AlertDescription>
              </Alert>
            )}
            {permissionsCheck.isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t('woocommerce.permissions.errorTitle')}</AlertTitle>
                <AlertDescription>
                  {permissionsCheck.error?.message ?? t('woocommerce.permissions.errorDescription')}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Plugin Version Check */}
          <div className="flex items-center gap-2 pt-2">
            {pluginVersionState === 'loading' && (
              <>
                <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />
                <span className="text-muted-foreground text-xs">{t('woocommerce.pluginVersion.checking')}</span>
              </>
            )}
            {pluginVersionState === 'never-seen' && (
              <span className="text-muted-foreground text-xs">{t('woocommerce.pluginVersion.neverSeen')}</span>
            )}
            {pluginVersionState === 'never-reported' && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t('woocommerce.pluginVersion.staleTitle')}</AlertTitle>
                <AlertDescription>{t('woocommerce.pluginVersion.neverReportedDescription')}</AlertDescription>
              </Alert>
            )}
            {pluginVersionState === 'unknown-current' && (
              <span className="text-muted-foreground text-xs">
                {t('woocommerce.pluginVersion.unknownCurrent', { version: installedPluginVersion?.version })}
              </span>
            )}
            {pluginVersionState === 'up-to-date' && (
              <Badge variant="green">
                <CheckCircle2 className="h-3 w-3" />
                {t('woocommerce.pluginVersion.upToDate', { version: installedPluginVersion?.version })}
              </Badge>
            )}
            {pluginVersionState === 'stale' && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t('woocommerce.pluginVersion.staleTitle')}</AlertTitle>
                <AlertDescription>
                  {t('woocommerce.pluginVersion.staleDescription', {
                    installed: installedPluginVersion?.version,
                    current: currentPluginVersion,
                  })}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sync Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{t('woocommerce.sync.title')}</CardTitle>
          <CardDescription>{t('woocommerce.sync.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="syncOrdersType">{t('woocommerce.sync.orderStatusLabel')}</Label>
            <Select
              value={editForm.watch('syncOrdersType')}
              onValueChange={(value) =>
                editForm.setValue('syncOrdersType', value as 'pending' | 'processing' | 'completed')
              }
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('woocommerce.sync.orderStatusPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">{t('woocommerce.sync.pending')}</SelectItem>
                <SelectItem value="processing">{t('woocommerce.sync.processing')}</SelectItem>
                <SelectItem value="completed">{t('woocommerce.sync.completed')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">{t('woocommerce.sync.orderStatusHelp')}</p>
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="pushOrder">{t('woocommerce.sync.pushOrderLabel')}</Label>
              <p className="text-muted-foreground text-xs">{t('woocommerce.sync.pushOrderHelp')}</p>
            </div>
            <Switch
              id="pushOrder"
              checked={editForm.watch('pushOrder')}
              onCheckedChange={(checked) => editForm.setValue('pushOrder', checked)}
              disabled={isLoading}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="pushRebillOrders">{t('woocommerce.sync.pushRebillOrdersLabel')}</Label>
              <p className="text-muted-foreground text-xs">{t('woocommerce.sync.pushRebillOrdersHelp')}</p>
            </div>
            <Switch
              id="pushRebillOrders"
              checked={editForm.watch('pushRebillOrders')}
              onCheckedChange={(checked) => editForm.setValue('pushRebillOrders', checked)}
              disabled={isLoading}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="autoSyncEnabled">{t('woocommerce.sync.autoSyncLabel')}</Label>
              <p className="text-muted-foreground text-xs">{t('woocommerce.sync.autoSyncHelp')}</p>
            </div>
            <Switch
              id="autoSyncEnabled"
              checked={editForm.watch('autoSyncEnabled')}
              onCheckedChange={(checked) => editForm.setValue('autoSyncEnabled', checked)}
              disabled={isLoading}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="useNativeOrderEmails">{t('woocommerce.sync.nativeOrderEmailsLabel')}</Label>
              <p className="text-muted-foreground text-xs">{t('woocommerce.sync.nativeOrderEmailsHelp')}</p>
            </div>
            <Switch
              id="useNativeOrderEmails"
              checked={editForm.watch('useNativeOrderEmails')}
              onCheckedChange={(checked) => editForm.setValue('useNativeOrderEmails', checked)}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Advanced Features */}
      <Card>
        <CardHeader>
          <CardTitle>{t('woocommerce.advancedFeaturesTitle')}</CardTitle>
          <CardDescription>{t('woocommerce.advancedFeaturesDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="wooDynamicDiscountEnabled"
                checked={editForm.watch('dynamicDiscountEnabled')}
                onCheckedChange={(checked) => editForm.setValue('dynamicDiscountEnabled', checked as boolean)}
                disabled={isLoading}
                className="mt-1"
              />
              <div className="space-y-1">
                <Label htmlFor="wooDynamicDiscountEnabled" className="cursor-pointer font-medium">
                  {t('woocommerce.dynamicDiscount')}
                </Label>
              </div>
            </div>

            {editForm.watch('dynamicDiscountEnabled') && (
              <div className="ml-7 space-y-2">
                <Label htmlFor="wooDynamicDiscountStrategy">{t('woocommerce.dynamicDiscountStrategy')}</Label>
                <Select
                  value={editForm.watch('dynamicDiscountStrategy')}
                  onValueChange={(value) =>
                    editForm.setValue(
                      'dynamicDiscountStrategy',
                      value as 'order-level' | 'line-item-allocation',
                    )
                  }
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('woocommerce.selectDiscountStrategy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order-level">{t('woocommerce.orderLevelDiscounts')}</SelectItem>
                    <SelectItem value="line-item-allocation">
                      {t('woocommerce.lineItemAllocation')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground mt-1 text-sm">{t('woocommerce.discountStrategyHelp')}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card>
        <CardHeader>
          <CardTitle>{t('woocommerce.webhooks.title')}</CardTitle>
          <CardDescription>{t('woocommerce.webhooks.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant={hasWebhookSecret ? 'outline' : 'default'}
            disabled={createWebhooks.isPending}
            onClick={handleGenerateWebhooks}
          >
            {createWebhooks.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {hasWebhookSecret
              ? t('woocommerce.webhooks.replaceButton')
              : t('woocommerce.webhooks.generateButton')}
          </Button>
          {hasWebhookSecret && (
            <p className="text-muted-foreground mt-2 text-xs">{t('woocommerce.webhooks.secretConfigured')}</p>
          )}
        </CardContent>
      </Card>

      <WooCommerceCheckoutPlugins storeId={resolvedStoreId} />

      {/* Timestamp */}
      {defaultValues.createdAt && (
        <p className="text-muted-foreground text-xs">
          {t('woocommerce.connectedSince')} {new Date(defaultValues.createdAt).toLocaleString()}
        </p>
      )}
    </form>
  );
}

// ============================================================================
// Troubleshoot Guide Component (Create Mode)
// ============================================================================

function WooCommerceTroubleshootGuide() {
  const [openItem, setOpenItem] = useState<string | null>(null);
  const { t } = useTranslation('integrations');

  const toggle = (key: string) => setOpenItem(openItem === key ? null : key);

  const items = [
    {
      key: 'rest-api',
      title: t('woocommerce.troubleshoot.restApi.title'),
      badge: t('woocommerce.troubleshoot.restApi.badge'),
      badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      content: (
        <>
          <p>{t('woocommerce.troubleshoot.restApi.requiresAccess')}</p>
          <code className="my-2 block rounded bg-slate-100 px-3 py-1.5 text-xs dark:bg-slate-800">
            https://yourdomain.com/wp-json/
          </code>
          <p className="font-medium">{t('woocommerce.troubleshoot.restApi.testIt')}</p>
          <p dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.restApi.testInstructions') }} />
          <p className="mt-2 font-medium">{t('woocommerce.troubleshoot.restApi.noJsonCheck')}</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li>{t('woocommerce.troubleshoot.restApi.firewallBlocking')}</li>
            <li>{t('woocommerce.troubleshoot.restApi.securityPluginBlocking')}</li>
            <li>{t('woocommerce.troubleshoot.restApi.wrongDomain')}</li>
          </ul>
        </>
      ),
    },
    {
      key: 'permalinks',
      title: t('woocommerce.troubleshoot.permalinks.title'),
      badge: t('woocommerce.troubleshoot.permalinks.badge'),
      badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      content: (
        <>
          <p dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.permalinks.description') }} />
          <p className="mt-2 font-medium">{t('woocommerce.troubleshoot.permalinks.fix')}</p>
          <ol className="mt-1 list-decimal space-y-1 pl-4">
            <li dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.permalinks.step1') }} />
            <li dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.permalinks.step2') }} />
            <li>{t('woocommerce.troubleshoot.permalinks.step3')}</li>
          </ol>
        </>
      ),
    },
    {
      key: 'ssl',
      title: t('woocommerce.troubleshoot.ssl.title'),
      content: (
        <>
          <p dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.ssl.description') }} />
          <p className="mt-2 font-medium">{t('woocommerce.troubleshoot.ssl.makeSure')}</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.ssl.siteLoadsHttps') }} />
            <li>{t('woocommerce.troubleshoot.ssl.validCertificate')}</li>
            <li>{t('woocommerce.troubleshoot.ssl.noMixedContent')}</li>
          </ul>
        </>
      ),
    },
    {
      key: 'security-plugin',
      title: t('woocommerce.troubleshoot.securityPlugin.title'),
      content: (
        <>
          <p dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.securityPlugin.description') }} />
          <p className="mt-2 font-medium">{t('woocommerce.troubleshoot.securityPlugin.try')}</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li>{t('woocommerce.troubleshoot.securityPlugin.disablePlugin')}</li>
            <li
              dangerouslySetInnerHTML={{
                __html: t('woocommerce.troubleshoot.securityPlugin.whitelistEndpoint'),
              }}
            />
          </ul>
        </>
      ),
    },
    {
      key: 'dns',
      title: t('woocommerce.troubleshoot.dns.title'),
      content: (
        <>
          <p>{t('woocommerce.troubleshoot.dns.description')}</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li>{t('woocommerce.troubleshoot.dns.apiCallsFail')}</li>
            <li>{t('woocommerce.troubleshoot.dns.cannotSendData')}</li>
          </ul>
          <p className="mt-2">{t('woocommerce.troubleshoot.dns.waitForPropagation')}</p>
        </>
      ),
    },
    {
      key: 'api-permissions',
      title: t('woocommerce.troubleshoot.apiPermissions.title'),
      badge: t('woocommerce.troubleshoot.apiPermissions.badge'),
      badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      content: (
        <>
          <p dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.apiPermissions.description') }} />
          <p className="mt-2 font-medium">{t('woocommerce.troubleshoot.apiPermissions.requirements')}</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li
              dangerouslySetInnerHTML={{
                __html: t('woocommerce.troubleshoot.apiPermissions.readWritePermission'),
              }}
            />
            <li
              dangerouslySetInnerHTML={{
                __html: t('woocommerce.troubleshoot.apiPermissions.adminOrShopManager'),
              }}
            />
          </ul>
          <p className="mt-2 font-medium">{t('woocommerce.troubleshoot.apiPermissions.howToCheck')}</p>
          <ol className="mt-1 list-decimal space-y-1 pl-4">
            <li dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.apiPermissions.step1') }} />
            <li dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.apiPermissions.step2') }} />
            <li>{t('woocommerce.troubleshoot.apiPermissions.step3')}</li>
            <li dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.apiPermissions.step4') }} />
          </ol>
          <p className="mt-2 text-slate-500 dark:text-slate-400">
            {t('woocommerce.troubleshoot.apiPermissions.hint')}
          </p>
        </>
      ),
    },
    {
      key: 'cloudflare',
      title: t('woocommerce.troubleshoot.cloudflare.title'),
      content: (
        <>
          <p dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.cloudflare.description') }} />
          <p className="mt-2 font-medium">{t('woocommerce.troubleshoot.cloudflare.check')}</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li>{t('woocommerce.troubleshoot.cloudflare.underAttackMode')}</li>
            <li dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.cloudflare.wafRules') }} />
            <li>{t('woocommerce.troubleshoot.cloudflare.noIpBlocking')}</li>
          </ul>
        </>
      ),
    },
    {
      key: 'bundles',
      title: t('woocommerce.troubleshoot.bundles.title'),
      badge: t('woocommerce.troubleshoot.bundles.badge'),
      badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      content: (
        <>
          <p dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.bundles.description') }} />
          <p className="mt-2 font-medium">{t('woocommerce.troubleshoot.bundles.whyTitle')}</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li>{t('woocommerce.troubleshoot.bundles.whyDynamic1')}</li>
            <li>{t('woocommerce.troubleshoot.bundles.whyDynamic2')}</li>
            <li>{t('woocommerce.troubleshoot.bundles.whyDynamic3')}</li>
          </ul>
          <p className="mt-2 font-medium">{t('woocommerce.troubleshoot.bundles.fixTitle')}</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li dangerouslySetInnerHTML={{ __html: t('woocommerce.troubleshoot.bundles.fix1') }} />
            <li>{t('woocommerce.troubleshoot.bundles.fix2')}</li>
            <li>{t('woocommerce.troubleshoot.bundles.fix3')}</li>
          </ul>
        </>
      ),
    },
  ];

  return (
    <Card className="mt-6 border-slate-200 dark:border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-slate-500" />
          <CardTitle className="text-sm">{t('woocommerce.troubleshoot.title')}</CardTitle>
        </div>
        <CardDescription className="text-xs">{t('woocommerce.troubleshoot.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((item) => (
          <Collapsible key={item.key} open={openItem === item.key} onOpenChange={() => toggle(item.key)}>
            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
              <span className="flex items-center gap-2">
                {item.title}
                {item.badge && (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${item.badgeClass}`}>
                    {item.badge}
                  </span>
                )}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-slate-500 transition-transform ${openItem === item.key ? 'rotate-180' : ''}`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-3 pb-2 pt-2 text-xs text-slate-600 dark:text-slate-400">
              {item.content}
            </CollapsibleContent>
          </Collapsible>
        ))}
      </CardContent>
    </Card>
  );
}
